import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import churchHero from "@/assets/church/church-hero-ministry.png.asset.json";
import bishopFlyer from "@/assets/church/church-bishop-flyer.jpeg.asset.json";
import {
  Phone, Calendar, PlayCircle, MessageSquare, Heart, Users, Clock, Sparkles,
  ShieldCheck, Wifi, Megaphone, Globe, Bot, BarChart3, Wrench, Calendar as CalIcon,
  MailOpen, Smartphone, BookOpen, HandHeart, CheckCircle2, ArrowRight,
} from "lucide-react";

const FLOATING_BENEFITS = [
  "24/7 Prayer Line", "AI Church Receptionist", "Visitor Follow-Up", "Member Communication",
  "Church Marketing", "Event Registration", "Volunteer Coordination", "Cleaner Facilities", "More Time For Ministry",
];

const MODULES = [
  { icon: Phone, title: "AI Receptionist", desc: "24/7 call answering, FAQ, routing." },
  { icon: HandHeart, title: "24/7 Prayer Line", desc: "Collect, route, and care for every request." },
  { icon: Users, title: "Visitor Follow-Up", desc: "Never lose a first-time guest again." },
  { icon: CalIcon, title: "Event Registration", desc: "Frictionless signups + reminders." },
  { icon: ShieldCheck, title: "Volunteer Management", desc: "Schedules, reminders, recognition." },
  { icon: Megaphone, title: "Church Marketing", desc: "Reach more people in your community." },
  { icon: Smartphone, title: "Social Media", desc: "Daily content built from your sermons." },
  { icon: MailOpen, title: "Email Campaigns", desc: "Beautiful weekly + event sends." },
  { icon: MessageSquare, title: "Text Messaging", desc: "Prayer, events, member care." },
  { icon: Heart, title: "Online Giving Comms", desc: "Donor touchpoints & receipts." },
  { icon: Globe, title: "Website Management", desc: "Modern, fast, mobile-first." },
  { icon: BookOpen, title: "Member Communication", desc: "Stay connected mid-week." },
  { icon: Wrench, title: "Cleaning Robotics", desc: "CC1 Pro, MT1, SH1 for facilities." },
  { icon: BarChart3, title: "Analytics Dashboard", desc: "Know what's happening in ministry." },
];

const PAIN_POINTS = [
  "Missed calls", "Missed visitors", "Missed prayer requests", "Staff burnout",
  "Volunteer shortages", "Administrative overload", "Communication breakdowns",
  "Event attendance struggles", "Facility maintenance demands",
];

const COMPARISON = [
  ["Calls after hours", "Goes to voicemail", "AI receptionist answers, routes, captures"],
  ["Prayer requests", "Lost in inboxes", "Routed to prayer team within minutes"],
  ["First-time visitors", "Forgotten by Tuesday", "Automated welcome + small-group invite"],
  ["Event reminders", "Hand-built every week", "Auto-sent across email + text"],
  ["Floor cleaning", "Volunteer burnout", "PUDU CC1 Pro on a nightly schedule"],
  ["Marketing", "Whoever has time", "Always-on social + ads built from sermons"],
];

const RECEPTIONIST_FEATURES = [
  "Answer Calls", "Schedule Meetings", "Provide Service Times", "Share Directions",
  "Handle FAQs", "Route Emergencies", "Collect Prayer Requests", "Connect To Staff",
  "Capture Visitors", "Recover Missed Calls", "Book Appointments",
];

const PRAYER_FEATURES = [
  "Prayer Request Collection", "Prayer Team Routing", "Pastoral Escalation",
  "Anonymous Requests", "Prayer Follow-Up", "Emergency Notifications",
  "Prayer Analytics", "Member Care Tracking",
];

const VISITOR_FLOW = [
  "Visitor Visits Church", "AI Follow-Up", "Welcome Text", "Email Sequence",
  "Small Group Invite", "Membership Journey", "Volunteer Opportunity", "Long-Term Engagement",
];

const MARKETING = [
  "Facebook Advertising", "Instagram Advertising", "Google Advertising", "YouTube Advertising",
  "Local SEO", "Website SEO", "Event Campaigns", "Revival Promotion", "Conference Promotion",
  "VBS Promotion", "Community Outreach", "Social Media Management", "AI Content Creation",
  "Sermon Repurposing", "Podcast Marketing", "Reputation Management",
];

const CONTENT_OUTPUTS = [
  "Facebook Posts", "Instagram Posts", "Short Videos", "YouTube Shorts", "TikTok Clips",
  "Email Newsletters", "Devotionals", "Blog Posts", "Event Promotions", "Member Updates",
];

const ADMIN_AUTOMATIONS = [
  "Scheduling", "Calendar Management", "Volunteer Management", "Attendance Tracking",
  "Event Registration", "Member Records", "Communications", "Forms",
  "Document Generation", "Reporting", "Analytics",
];

const WEBSITE_FEATURES = [
  "Online Giving", "Prayer Requests", "Event Calendars", "Live Streaming",
  "Member Portals", "Volunteer Portals", "Sermon Archives", "SEO Optimization",
  "Mobile Optimization", "Visitor Funnels",
];

const ROBOTS = [
  { name: "PUDU CC1 Pro", use: "Sanctuary & lobby autonomous cleaning" },
  { name: "PUDU MT1", use: "Mid-size fellowship halls & hallways" },
  { name: "PUDU MT1 Max", use: "Large facilities, multi-zone scheduling" },
  { name: "PUDU SH1", use: "Heavy-duty scrubbing for high-traffic floors" },
];

const ANALYTICS_ITEMS = [
  "Visitors", "Prayer Requests", "Attendance", "Calls", "Appointments",
  "Volunteers", "Events", "Campaigns", "Giving Communications",
];

const WHY_NEIL_AI = [
  "Faith-Friendly", "US-Based Support", "AI Experts", "PUDU Authorized Dealer",
  "Church-Focused", "24/7 Assistance", "Training Included", "Deployment Included", "Long-Term Partnership",
];

const Churches = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Church Technology Solutions",
    provider: { "@type": "Organization", name: "Neil AI Global Solutions" },
    serviceType: "AI Automation, Robotics, and Marketing for Churches",
    areaServed: "United States",
    description: "AI receptionist, 24/7 prayer line, visitor follow-up, church marketing, websites, cleaning robotics, and administrative AI for modern ministries.",
  };

  return (
    <>
      <SEO
        title="Church Technology Solutions | AI, Marketing & Robotics for Ministries | Neil AI"
        description="Your ministry was made for more. AI receptionist, 24/7 prayer line, visitor follow-up, church marketing, websites, and PUDU cleaning robotics — built for modern churches."
        canonical="https://www.neilai.ai/solutions/churches"
        keywords="Church AI, Church Automation, Church Receptionist AI, Church Prayer Line, Church Visitor Follow-Up, Church Marketing Services, Church Cleaning Robots, Church Technology Solutions, Church AI Assistant, Church Communications Platform, Church Operations Software, Church Growth Technology, Church Administrative AI, Church Robotics"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="min-h-screen bg-[#05070A] text-white">
        <Navigation />

        {/* HERO */}
        <section className="relative min-h-[92vh] flex items-center overflow-hidden pt-32 md:pt-40 pb-16">
          <img src={churchHero.url} alt="Neil AI Church Automation — Your Ministry Was Made For More" className="absolute inset-0 w-full h-full object-cover object-center" width={1920} height={960} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#05070A] via-[#05070A]/85 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05070A] via-transparent to-transparent" />

          <div className="relative max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center w-full">
            <div>
              <Badge className="mb-5 bg-[#D6A84F]/15 text-[#D6A84F] border border-[#D6A84F]/30">Church Solutions</Badge>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05]">
                Your Ministry Was{" "}
                <span className="bg-gradient-to-r from-[#D6A84F] via-[#FFD700] to-[#D6A84F] bg-clip-text text-transparent">Made For More.</span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-slate-300/95 max-w-xl">
                Let AI, automation, marketing, and robotics handle repetitive tasks so your team can focus on people.
              </p>
              <p className="mt-4 text-sm text-slate-400 italic">We don't replace people. We support and elevate them.</p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="bg-gradient-to-r from-[#D6A84F] to-[#FFD700] text-black font-bold h-14 px-6">
                  <Link to="/book-demo"><Calendar className="w-5 h-5 mr-2" />Schedule Church Tech Assessment</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/20 h-14 px-6 hover:bg-white/10">
                  <a href="#prayer"><PlayCircle className="w-5 h-5 mr-2" />Watch Live Demo</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/20 h-14 px-6 hover:bg-white/10">
                  <a href="tel:+13362033184"><Phone className="w-5 h-5 mr-2" />Talk To A Church Specialist</a>
                </Button>
              </div>

              <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-400">
                <span>Serving Churches Across America</span><span>·</span>
                <span>AI Employees</span><span>·</span>
                <span>PUDU Robotics</span><span>·</span>
                <span>24/7 Support</span>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="grid grid-cols-2 gap-3">
                {FLOATING_BENEFITS.slice(0, 8).map((b) => (
                  <div key={b} className="px-4 py-3 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10 text-sm text-slate-200 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#00F5A0] shrink-0" /> {b}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CHALLENGE */}
        <section className="py-20 md:py-28 bg-[#05070A]">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-white/5 text-white/70 border border-white/10">The Modern Church Challenge</Badge>
              <h2 className="text-3xl md:text-5xl font-bold">Ministry is harder than ever.<br />Your team shouldn't have to do it alone.</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-12">
              {PAIN_POINTS.map((p) => (
                <div key={p} className="p-4 rounded-xl bg-white/[0.03] border border-white/10 text-slate-300 text-sm">• {p}</div>
              ))}
            </div>
            <Card className="bg-white/[0.03] border-white/10 overflow-hidden">
              <div className="grid md:grid-cols-3 text-sm">
                <div className="p-5 md:p-6 font-semibold text-slate-400 border-b md:border-b-0 md:border-r border-white/10">Workflow</div>
                <div className="p-5 md:p-6 font-semibold text-slate-300 border-b md:border-b-0 md:border-r border-white/10">Traditional Operations</div>
                <div className="p-5 md:p-6 font-semibold text-[#00F5A0]">With Neil AI</div>
                {COMPARISON.map(([w, t, n]) => (
                  <div key={w} className="contents">
                    <div className="p-5 md:p-6 border-t border-white/10 text-slate-200">{w}</div>
                    <div className="p-5 md:p-6 border-t border-white/10 text-slate-400">{t}</div>
                    <div className="p-5 md:p-6 border-t border-white/10 text-white">{n}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* CHURCH OS */}
        <section className="py-20 md:py-28 bg-gradient-to-b from-[#05070A] via-[#071A33]/30 to-[#05070A]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-[#00A3FF]/10 text-[#00A3FF] border border-[#00A3FF]/30">The Church Operating System™</Badge>
              <h2 className="text-3xl md:text-5xl font-bold">One Platform. <span className="text-[#00A3FF]">Every Ministry.</span></h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {MODULES.map((m) => (
                <Card key={m.title} className="p-5 bg-white/[0.03] border-white/10 hover:border-[#00A3FF]/50 hover:bg-white/[0.05] transition-all">
                  <m.icon className="w-7 h-7 text-[#00F5A0] mb-3" />
                  <h3 className="font-bold mb-1">{m.title}</h3>
                  <p className="text-sm text-slate-400">{m.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* RECEPTIONIST */}
        <section className="py-20 md:py-28">
          <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-[#00F5A0]/10 text-[#00F5A0] border border-[#00F5A0]/30">AI Church Receptionist</Badge>
              <h2 className="text-3xl md:text-5xl font-bold">A Church Secretary That Never Sleeps.</h2>
              <p className="mt-4 text-slate-300">24 hours · 365 days · Multilingual · Voice + SMS</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-2">
                {RECEPTIONIST_FEATURES.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm text-slate-200"><CheckCircle2 className="w-4 h-4 text-[#00F5A0]" />{f}</div>
                ))}
              </div>
              <Button asChild className="mt-8 bg-gradient-to-r from-[#00F5A0] to-[#00D9F5] text-[#05070A] font-bold"><Link to="/book-demo">Launch AI Receptionist</Link></Button>
            </div>
            <Card className="p-6 bg-white/[0.03] border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-[#00F5A0] animate-pulse" /><span className="text-xs uppercase text-slate-400">Live calls</span></div>
                <span className="text-xs text-slate-400">Today</span>
              </div>
              {[
                ["08:14", "Service times Sunday?", "Answered"],
                ["09:02", "Prayer for my mother", "Routed to prayer team"],
                ["10:38", "First-time visitor", "Welcome sequence started"],
                ["13:21", "Pastor meeting request", "Booked Thu 2pm"],
              ].map(([t, msg, action]) => (
                <div key={t} className="py-3 border-t border-white/10 first:border-t-0 flex items-center justify-between gap-4">
                  <div><div className="text-sm text-white">{msg}</div><div className="text-xs text-slate-400">{action}</div></div>
                  <span className="text-xs text-slate-500">{t}</span>
                </div>
              ))}
            </Card>
          </div>
        </section>

        {/* PRAYER */}
        <section id="prayer" className="py-20 md:py-28 bg-[#071A33]/30">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <Badge className="mb-4 bg-[#D6A84F]/15 text-[#D6A84F] border border-[#D6A84F]/30">24/7 Prayer Line</Badge>
            <h2 className="text-3xl md:text-5xl font-bold">Prayer Never Closes.</h2>
            <p className="mt-4 text-slate-300 max-w-2xl mx-auto">A dedicated line for prayer — answered with care, routed to your team, followed up with grace.</p>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {PRAYER_FEATURES.map((f) => (
                <Card key={f} className="p-4 bg-white/[0.03] border-white/10 text-sm text-slate-200">{f}</Card>
              ))}
            </div>
            <Button asChild className="mt-10 bg-gradient-to-r from-[#D6A84F] to-[#FFD700] text-black font-bold"><Link to="/book-demo">Launch A Prayer Line</Link></Button>
          </div>
        </section>

        {/* VISITOR */}
        <section className="py-20 md:py-28">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-[#00A3FF]/10 text-[#00A3FF] border border-[#00A3FF]/30">Visitor Follow-Up Engine</Badge>
              <h2 className="text-3xl md:text-5xl font-bold">Never Lose A First-Time Visitor Again.</h2>
            </div>
            <div className="flex flex-col items-center gap-2">
              {VISITOR_FLOW.map((step, i) => (
                <div key={step} className="w-full max-w-md">
                  <Card className="p-4 bg-white/[0.03] border-white/10 text-center">
                    <span className="text-xs text-[#00A3FF] font-mono">Step {i + 1}</span>
                    <div className="font-semibold mt-1">{step}</div>
                  </Card>
                  {i < VISITOR_FLOW.length - 1 && <div className="text-[#00F5A0] text-center py-1">↓</div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MARKETING */}
        <section className="py-20 md:py-28 bg-[#071A33]/30">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-[#D6A84F]/15 text-[#D6A84F] border border-[#D6A84F]/30">Church Marketing Division</Badge>
              <h2 className="text-3xl md:text-5xl font-bold">Reach More People In Your Community.</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {MARKETING.map((s) => (
                <div key={s} className="p-4 rounded-xl bg-white/[0.03] border border-white/10 text-sm text-slate-200">{s}</div>
              ))}
            </div>
          </div>
        </section>

        {/* CONTENT STUDIO */}
        <section className="py-20 md:py-28">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <Badge className="mb-4 bg-[#00A3FF]/10 text-[#00A3FF] border border-[#00A3FF]/30">AI Content Studio</Badge>
            <h2 className="text-3xl md:text-5xl font-bold">One Sermon. Unlimited Content.</h2>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {CONTENT_OUTPUTS.map((o) => (
                <Card key={o} className="p-4 bg-white/[0.03] border-white/10 text-sm">{o}</Card>
              ))}
            </div>
          </div>
        </section>

        {/* ADMIN */}
        <section className="py-20 md:py-28 bg-[#071A33]/30">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-[#00F5A0]/10 text-[#00F5A0] border border-[#00F5A0]/30">Church Administrative AI</Badge>
              <h2 className="text-3xl md:text-5xl font-bold">Reduce Administrative Overload.</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {ADMIN_AUTOMATIONS.map((a) => (
                <Card key={a} className="p-4 bg-white/[0.03] border-white/10 text-sm flex items-center gap-2"><Bot className="w-4 h-4 text-[#00F5A0]" />{a}</Card>
              ))}
            </div>
          </div>
        </section>

        {/* WEBSITES */}
        <section className="py-20 md:py-28">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <Badge className="mb-4 bg-[#00A3FF]/10 text-[#00A3FF] border border-[#00A3FF]/30">Church Website Systems</Badge>
            <h2 className="text-3xl md:text-5xl font-bold">Modern Websites Built For Ministry.</h2>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {WEBSITE_FEATURES.map((f) => (
                <Card key={f} className="p-4 bg-white/[0.03] border-white/10 text-sm">{f}</Card>
              ))}
            </div>
          </div>
        </section>

        {/* CLEANING ROBOTS */}
        <section className="py-20 md:py-28 bg-[#071A33]/30">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-[#D6A84F]/15 text-[#D6A84F] border border-[#D6A84F]/30">Church Cleaning Robotics</Badge>
              <h2 className="text-3xl md:text-5xl font-bold">Cleaner Facilities. Healthier Environments.</h2>
              <p className="mt-3 text-slate-300">Sanctuary · Fellowship Hall · Classrooms · Hallways · Lobby</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {ROBOTS.map((r) => (
                <Card key={r.name} className="p-5 bg-white/[0.03] border-white/10">
                  <Wrench className="w-7 h-7 text-[#00F5A0] mb-3" />
                  <h3 className="font-bold">{r.name}</h3>
                  <p className="text-sm text-slate-400 mt-1">{r.use}</p>
                </Card>
              ))}
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              {["Consistent Cleaning", "Reduced Burnout", "Lower Costs", "Improved Appearance", "Safer Floors", "Event Cleanup"].map((b) => (
                <div key={b} className="p-4 rounded-xl bg-white/[0.03] border border-white/10 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#00F5A0]" />{b}</div>
              ))}
            </div>
          </div>
        </section>

        {/* ANALYTICS */}
        <section className="py-20 md:py-28">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <Badge className="mb-4 bg-[#00A3FF]/10 text-[#00A3FF] border border-[#00A3FF]/30">Church Analytics Center</Badge>
            <h2 className="text-3xl md:text-5xl font-bold">Know What's Happening In Your Ministry.</h2>
            <div className="mt-10 grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {ANALYTICS_ITEMS.map((a) => (
                <Card key={a} className="p-4 bg-white/[0.03] border-white/10 text-sm flex items-center justify-center gap-2"><BarChart3 className="w-4 h-4 text-[#00F5A0]" />{a}</Card>
              ))}
            </div>
          </div>
        </section>

        {/* WHY */}
        <section className="py-20 md:py-28 bg-[#071A33]/30">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-bold">Why Churches Choose Neil AI</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {WHY_NEIL_AI.map((w) => (
                <div key={w} className="p-4 rounded-xl bg-white/[0.03] border border-white/10 flex items-center gap-3"><Sparkles className="w-5 h-5 text-[#D6A84F]" /><span>{w}</span></div>
              ))}
            </div>
          </div>
        </section>

        {/* CHURCH INTEGRATION CONTACT — Bishop Albert Saunders Sr */}
        <section className="py-20 md:py-28 bg-gradient-to-b from-[#05070A] via-[#071A33]/40 to-[#05070A]">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-[#D6A84F]/15 text-[#D6A84F] border border-[#D6A84F]/30">Church Integration Contact</Badge>
              <h2 className="text-3xl md:text-5xl font-bold">Your Direct Line To Church Integration</h2>
              <p className="mt-4 text-slate-300 max-w-2xl mx-auto">
                Every Neil AI church partnership is overseen by our Chief Financial Officer and ministry leader — making sure your church is supported with care, integrity, and the highest standard of excellence.
              </p>
            </div>

            <Card className="overflow-hidden bg-white/[0.03] border-white/10 grid lg:grid-cols-2">
              <div className="relative min-h-[320px] lg:min-h-[520px] bg-[#05070A]">
                <img
                  src={bishopFlyer.url}
                  alt="Bishop Albert Saunders Sr, Chief Financial Officer, Neil AI Global Solutions — Church Integration"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  width={1290}
                  height={1024}
                />
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <Badge className="self-start mb-4 bg-[#00A3FF]/10 text-[#00A3FF] border border-[#00A3FF]/30">Bishop · CFO</Badge>
                <h3 className="text-3xl md:text-4xl font-bold">Bishop Albert Saunders Sr.</h3>
                <p className="mt-2 text-[#D6A84F] font-semibold uppercase tracking-wide text-sm">
                  Chief Financial Officer · Neil AI Global Solutions LLC
                </p>
                <p className="mt-5 text-slate-300">
                  Bishop Saunders personally leads our Church Integration program — guiding pastors and ministry teams through AI receptionists, 24/7 prayer lines, visitor follow-up, member communications, and PUDU cleaning robotics.
                </p>

                <blockquote className="mt-6 border-l-2 border-[#D6A84F] pl-4 italic text-slate-200">
                  "We don't replace people. We support and elevate them — so ministry teams can focus on serving God's people."
                </blockquote>

                <div className="mt-7 space-y-3 text-slate-200">
                  <a href="tel:+13367697453" className="flex items-center gap-3 hover:text-[#00F5A0] transition-colors">
                    <Phone className="w-5 h-5 text-[#00F5A0]" /> <span className="font-semibold">(336) 769-7453</span>
                    <span className="text-xs text-slate-400">— Direct line</span>
                  </a>
                  <a href="tel:+13369624539" className="flex items-center gap-3 hover:text-[#00F5A0] transition-colors">
                    <Phone className="w-5 h-5 text-[#00F5A0]" /> <span className="font-semibold">(336) 962-4539</span>
                    <span className="text-xs text-slate-400">— Office</span>
                  </a>
                  <a href="mailto:info@neilaiglobalsolutionsllc.net" className="flex items-center gap-3 hover:text-[#00F5A0] transition-colors break-all">
                    <MailOpen className="w-5 h-5 text-[#00F5A0]" /> info@neilaiglobalsolutionsllc.net
                  </a>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Button asChild size="lg" className="bg-gradient-to-r from-[#D6A84F] to-[#FFD700] text-black font-bold">
                    <Link to="/book-demo"><Calendar className="w-5 h-5 mr-2" />Schedule Free Church Consultation</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="border-white/20 hover:bg-white/10">
                    <a href="tel:+13367697453"><Phone className="w-5 h-5 mr-2" />Call Bishop Saunders</a>
                  </Button>
                </div>

                <p className="mt-6 text-xs text-slate-500 italic">
                  "Therefore, my beloved brethren, be ye steadfast, unmovable, always abounding in the work of the Lord…" — 1 Corinthians 15:58
                </p>
              </div>
            </Card>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-24 md:py-32">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-6xl font-bold leading-tight">
              Focus On Ministry.<br />
              <span className="bg-gradient-to-r from-[#D6A84F] to-[#FFD700] bg-clip-text text-transparent">We'll Handle The Technology.</span>
            </h2>
            <p className="mt-6 text-slate-300">AI Employees · Church Marketing · Prayer Lines · Visitor Follow-Up · Websites · Automation · PUDU Robotics · 24/7 Support</p>
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <Button asChild size="lg" className="bg-gradient-to-r from-[#D6A84F] to-[#FFD700] text-black font-bold h-14 px-7"><Link to="/book-demo"><Calendar className="w-5 h-5 mr-2" />Schedule Free Church Assessment</Link></Button>
              <Button asChild size="lg" variant="outline" className="border-white/20 h-14 px-7 hover:bg-white/10"><Link to="/book-demo">Book Live Demo</Link></Button>
              <Button asChild size="lg" variant="outline" className="border-white/20 h-14 px-7 hover:bg-white/10"><a href="tel:+13362033184"><Phone className="w-5 h-5 mr-2" />Call Neil AI Now</a></Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};
export default Churches;
